const TRIAGE_DIMENSIONS = [
  "accessibility",
  "ai-vulnerability",
  "broken-links",
  "cognitive-load",
  "stale-references",
  "stale-concepts",
  "content-objective-mapping",
  "industry-alignment",
  "institutional-guideline",
  "rubric-alignment",
  "vision-style",
] as const;

const TRIAGE_SURFACES = ["overview", "dimension", "entity"] as const;

const TRIAGE_SHEET_STATES = ["open", "closed"] as const;

export type TriageDimension = (typeof TRIAGE_DIMENSIONS)[number];
export type TriageSurface = (typeof TRIAGE_SURFACES)[number];
export type TriageSheetState = (typeof TRIAGE_SHEET_STATES)[number];

export interface TriageQueryState {
  courseId: string | null;
  dimension: TriageDimension | null;
  entityId: string | null;
  surface: TriageSurface;
  sheet: TriageSheetState;
}

export function resolveReviewCourseId(
  routeCourseId: string | null | undefined,
  queryCourseId: string | null | undefined
): string | undefined {
  return routeCourseId?.trim() || queryCourseId?.trim() || undefined;
}

interface BuildTriageQueryInput {
  courseId?: string | null;
  dimension?: TriageDimension | null;
  entityId?: string | null;
  surface?: TriageSurface | null;
  sheet?: TriageSheetState | null;
}

function parseEnumParam<T extends readonly string[]>(
  params: URLSearchParams,
  key: string,
  allowedValues: T
): T[number] | null {
  const rawValue = params.get(key)?.trim().toLowerCase();
  if (!rawValue) {
    return null;
  }

  const matchedValue = allowedValues.find((value) => value === rawValue);
  return matchedValue ?? null;
}

function parseStringParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim();
  return value ? value : null;
}

export function parseTriageQueryState(search: string): TriageQueryState {
  const params = new URLSearchParams(search);

  return {
    courseId: parseStringParam(params, "courseId"),
    dimension: parseEnumParam(params, "dimension", TRIAGE_DIMENSIONS),
    entityId: parseStringParam(params, "entityId"),
    surface: parseEnumParam(params, "surface", TRIAGE_SURFACES) ?? "overview",
    sheet: parseEnumParam(params, "sheet", TRIAGE_SHEET_STATES) ?? "closed",
  };
}

export function buildTriageQueryState(search: string, input: BuildTriageQueryInput): string {
  const params = new URLSearchParams(search);

  if (input.courseId === undefined) {
    // keep existing param as-is
  } else if (input.courseId) {
    params.set("courseId", input.courseId);
  } else {
    params.delete("courseId");
  }

  if (input.dimension === undefined) {
    // keep existing param as-is
  } else if (input.dimension) {
    params.set("dimension", input.dimension);
  } else {
    params.delete("dimension");
  }

  if (input.entityId === undefined) {
    // keep existing param as-is
  } else if (input.entityId) {
    params.set("entityId", input.entityId);
  } else {
    params.delete("entityId");
  }

  if (input.surface === undefined) {
    // keep existing param as-is
  } else if (input.surface) {
    params.set("surface", input.surface);
  } else {
    params.delete("surface");
  }

  if (input.sheet === undefined) {
    // keep existing param as-is
  } else if (input.sheet) {
    params.set("sheet", input.sheet);
  } else {
    params.delete("sheet");
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}
